import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { CheckCircle, FileText, TrendingUp, Shield } from 'lucide-react';

const LeadCaptureModal = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  toolName,
  isSubmitting = false 
}) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: ''
  });

  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'WhatsApp number is required';
    } else if (!/^\d{10}$/.test(formData.phone.trim())) {
      newErrors.phone = 'Please enter a valid 10-digit mobile number';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const handleClose = () => {
    setFormData({ name: '', phone: '' });
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-3xl font-bold text-center text-gray-800 mb-2">
            Get Your Comprehensive Wealth Roadmap
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 px-1">
          <p className="text-center text-base md:text-lg text-gray-600">
            Unlock the full report to see:
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="flex items-start gap-2 md:gap-3 p-3 md:p-4 bg-blue-50 rounded-lg">
              <TrendingUp className="w-5 h-5 md:w-6 md:h-6 text-blue-600 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold text-sm md:text-base text-gray-800">Year-by-Year Impact</h4>
                <p className="text-xs md:text-sm text-gray-600">Detailed inflation and growth projections</p>
              </div>
            </div>
            
            <div className="flex items-start gap-2 md:gap-3 p-3 md:p-4 bg-green-50 rounded-lg">
              <FileText className="w-5 h-5 md:w-6 md:h-6 text-green-600 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold text-sm md:text-base text-gray-800">Recommended Funds</h4>
                <p className="text-xs md:text-sm text-gray-600">Top 4-5 funds for this specific goal</p>
              </div>
            </div>
            
            <div className="flex items-start gap-2 md:gap-3 p-3 md:p-4 bg-orange-50 rounded-lg">
              <Shield className="w-5 h-5 md:w-6 md:h-6 text-orange-600 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold text-sm md:text-base text-gray-800">Risk Analysis</h4>
                <p className="text-xs md:text-sm text-gray-600">Comprehensive risk assessment</p>
              </div>
            </div>
            
            <div className="flex items-start gap-2 md:gap-3 p-3 md:p-4 bg-purple-50 rounded-lg">
              <CheckCircle className="w-5 h-5 md:w-6 md:h-6 text-purple-600 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold text-sm md:text-base text-gray-800">PDF Report</h4>
                <p className="text-xs md:text-sm text-gray-600">Delivered directly via WhatsApp</p>
              </div>
            </div>
          </div>
          
          <div className="border-t pt-4 md:pt-6">
            <h3 className="text-lg md:text-xl font-semibold text-center mb-3 md:mb-4 text-gray-800">
              Enter your details to receive the report
            </h3>
            
            <div className="space-y-3 md:space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm md:text-base font-semibold text-gray-700">
                  Full Name *
                </Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter your full name"
                  className={`text-sm md:text-base p-2 md:p-3 border-2 ${errors.name ? 'border-red-500' : 'border-gray-300'} focus:border-blue-800`}
                  disabled={isSubmitting}
                />
                {errors.name && (
                  <p className="text-xs md:text-sm text-red-600">{errors.name}</p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-sm md:text-base font-semibold text-gray-700">
                  WhatsApp Number *
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="Enter 10-digit mobile number"
                  maxLength="10"
                  className={`text-sm md:text-base p-2 md:p-3 border-2 ${errors.phone ? 'border-red-500' : 'border-gray-300'} focus:border-blue-800`}
                  disabled={isSubmitting}
                />
                {errors.phone && (
                  <p className="text-xs md:text-sm text-red-600">{errors.phone}</p>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
            <Button
              onClick={handleClose}
              variant="outline"
              className="flex-1 border-2 border-gray-400 text-gray-700 hover:bg-gray-100 py-2 md:py-3 text-sm md:text-base font-semibold"
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              className="flex-1 bg-gradient-to-r from-blue-800 to-green-700 hover:from-blue-900 hover:to-green-800 text-white py-2 md:py-3 text-sm md:text-base font-semibold"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Get Full Report'}
            </Button>
          </div>
          
          <p className="text-xs text-center text-gray-500 pb-2">
            We respect your privacy. Your information will only be used to send you the detailed financial roadmap.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LeadCaptureModal;
